import { ReactNode } from "react";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import ArrowUpRed from "@Icons/ArrowUpRed";
import ArrowDownGreen from "@Icons/ArrowDownGreen";
import ArrowDownRed from "@Icons/ArrowDownRed";
import InfoIcon from "@Icons/InfoIcon";
import { ClimateData, ClimateIndicator } from "../types";
import { resolveCopy } from "../utils";
import { backendTrendIcon, factStatement, renderFactText } from "../utils/factDisplay";
import renderRichText from "../utils/renderRichText";
import { LeafIcon, EarthquakeIcon, SnowflakeIcon } from "../icons";
import NeighbourhoodCard from "./NeighbourhoodCard";
import CARD_ICONS from "./cardAssets";
import styles from "../neighbourhoodSection.module.scss";

const KEY_PREFIX = "propertypage.components.neighbourhood.climate";

// Flood and Wildfire are intentionally absent — P1D-5482 FR-4 removes them from
// the section entirely.
const CLIMATE_ICON_MAP: Record<string, ReactNode> = {
  [`${KEY_PREFIX}.badge.airQuality`]: <LeafIcon />,
  [`${KEY_PREFIX}.badge.earthquake`]: <EarthquakeIcon />,
  [`${KEY_PREFIX}.badge.snowfall`]: <SnowflakeIcon />,
};

type ClimateTabProps = {
  data: ClimateData;
};

const ClimateTab = ({ data }: ClimateTabProps) => {
  const { t } = useTranslationHook();

  const getValueClass = (level: string) => {
    switch (level) {
      case "low":
        return styles.climateValueLow;
      case "moderate":
      case "high":
        return styles.climateValueModerate;
      default:
        return "";
    }
  };

  const resolveIndicatorValue = (indicator: ClimateIndicator) => {
    if (indicator.value.kind === "key") {
      return resolveCopy(t, indicator.value.key);
    }
    return indicator.value.text;
  };

  // P1D-5540: with backend sentences, every data-driven string comes from the API.
  const { backend } = data;
  const cardStat = backend ? backend.card.stat : data.avgTemp;
  const tempLabel = backend ? backend.card.subtext : resolveCopy(t, data.tempLabelKey);
  const footerValue = backend
    ? backend.card.footerValue && renderRichText(backend.card.footerValue)
    : data.seasonalRange && <strong>{data.seasonalRange}</strong>;
  const source = backend?.source ?? resolveCopy(t, data.sourceKey);
  const sourceLabel = resolveCopy(t, "propertypage.components.neighbourhood.source.label");

  // Rows whose copy resolves to nothing are dropped along with their divider
  // (P1D-5482): a missing data point leaves no gap and no stray rule.
  const renderedFacts = data.facts
    .map((fact) => ({ fact, statement: factStatement(t, fact) }))
    .filter(({ statement }) => !!statement);

  return (
    <div className={styles.contentArea}>
      <NeighbourhoodCard
        image={data.heroImage}
        icon={CARD_ICONS.climate}
        label={resolveCopy(t, `${KEY_PREFIX}.card.label`)}
        stat={cardStat}
        subtext={tempLabel}
        alt="Climate neighbourhood view"
        footer={
          footerValue ? (
            <>
              {resolveCopy(t, `${KEY_PREFIX}.footer.seasonalRange`)} &middot; {footerValue}
            </>
          ) : undefined
        }
      />
      <div className={styles.detailsSection}>
        <div>
          {data.indicators.length > 0 && (
            <div className={styles.climateIndicators}>
              {data.indicators.map((indicator) => (
                <div key={indicator.labelKey} className={styles.climateIndicator}>
                  <div className={styles.climateIcon}>{CLIMATE_ICON_MAP[indicator.labelKey]}</div>
                  <span className={styles.climateLabel}>{resolveCopy(t, indicator.labelKey)}</span>
                  <span
                    className={formatClassNames(
                      styles.climateValue,
                      getValueClass(indicator.level),
                    )}
                  >
                    {resolveIndicatorValue(indicator)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {renderedFacts.map(({ fact, statement }, index) => {
            // Special case: cold winter gets ArrowDownRed instead of ArrowDownGreen
            const isColdWinter = fact.key === `${KEY_PREFIX}.fact.winter.cold`;

            return (
              <div key={fact.key}>
                <div
                  className={formatClassNames(styles.tableRow, index === 0 && styles.tableRowFirst)}
                >
                  <span className={styles.tableText}>{renderFactText(fact, statement)}</span>
                  <span className={styles.tableIcon} aria-hidden="true">
                    {backendTrendIcon(fact) ?? (
                      <>
                        {fact.trend === "up" && <ArrowUpRed />}
                        {fact.trend === "down" &&
                          (isColdWinter ? <ArrowDownRed /> : <ArrowDownGreen />)}
                      </>
                    )}
                  </span>
                </div>
                {index < renderedFacts.length - 1 && <div className={styles.tableDivider} />}
              </div>
            );
          })}
        </div>
        <p className={styles.sourceText}>
          <span className={styles.sourceIcon} aria-hidden="true">
            <InfoIcon width="14" height="14" fill="#6b7280" />
          </span>
          {sourceLabel} : {source}
        </p>
      </div>
    </div>
  );
};

export default wrapperHOC(ClimateTab, {
  componentName: "ClimateTab",
  showForChina: false,
  showForWhiteLable: false,
});
